import { Box, Typography } from '@mui/material';
import { getExposureText } from '../utils/reports';
import ReportNasemScoreBadge from './ReportNasemScoreBadge';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

type ReportNasemScoreBadgeAndTextProps = {
  nasemScore: number;
  userPercentile: number;
  showBadge: boolean;
  hasAboveRange?: boolean;
};

const ReportNasemScoreBadgeAndText: React.FC<ReportNasemScoreBadgeAndTextProps> = ({
  nasemScore,
  userPercentile,
  showBadge,
  hasAboveRange = false,
}) => {
  const { theme } = useThemeContext();
  const { exposureText, exposureLevel } = getExposureText(userPercentile);
  const relativePercentile = 100 - userPercentile;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        backgroundColor: theme.custom.report.comparison.reportNasemScoreBadgeAndText.backgroundColor,
        borderRadius: '12px',
        width: '100%',
        margin: '16px auto',
      }}
    >
      {showBadge && <ReportNasemScoreBadge nasemScore={nasemScore} />}

      {/* Divider Line */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          height: '40px',
          width: '1px',
          backgroundColor: theme.custom.report.comparison.reportNasemScoreBadgeAndText.dividerLineColor,
          margin: '0 16px',
        }}
      />

      {/* Text Content */}
      <Box
        sx={{
          textAlign: 'left',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            color: theme.custom.report.comparison.reportNasemScoreBadgeAndText.yourNasemScoreColor,
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          {userPercentile > 95
            ? 'According to NHANES, your score is in the highest 5% of the US general population.'
            : userPercentile <= 8
              ? `According to NHANES, around ${userPercentile}% of the US general population have no or extremely low PFAS levels like you.`
              : userPercentile <= 50
                ? `Your NASEM Score is lower than ${relativePercentile}% of the US general population.`
                : `Your NASEM Score is higher than ${userPercentile}% of the US general population.`}
        </Typography>
        <Typography
          sx={{
            color: theme.custom.report.comparison.reportNasemScoreBadgeAndText.exposureColor[exposureLevel].color,
            fontSize: '16px',
            fontWeight: 400,
            marginTop: '4px',
          }}
        >
          {exposureText}
        </Typography>
        {hasAboveRange && (
          <Typography
            sx={{
              color: theme.custom.report.comparison.reportNasemScoreBadgeAndText.yourNasemScoreColor,
              fontSize: '16px',
              fontWeight: 700,
              marginTop: '4px',
            }}
          >
            At least one of your results is too high to accurately quantify. This means the number you see here is the
            minimum expected level for your NASEM score. This means your result is exceptionally high.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ReportNasemScoreBadgeAndText;
